import { Button, Card, Form, Input, Modal, Switch, Typography } from 'antd'
import {
  CloseOutlined,
  DownloadOutlined,
  SettingOutlined,
} from '@ant-design/icons'

import React, { useCallback, useEffect, useState } from 'react'
import {
  createEvoCRMIntegration,
  deleteEvoCRMIntegration,
  getEvoCRMIntegration,
  getEvoCRMPanelsIntegration,
  updateEvoCRMIntegration,
} from '../../services/evo-crm-integration'
import { InputSearch } from '../input-search'
import { toast } from 'sonner'

export function EvoCRMIntegration({ icon, label, assistantID }) {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState()
  const [buttonLoading, setButtonLoading] = useState(false)
  const [configOpen, setConfigOpen] = useState(false)
  const [form] = Form.useForm()
  const autoCreateCard = Form.useWatch('autoCreateCard', form)

  useEffect(() => {
    setLoading(true)
    getEvoCRMIntegration(assistantID)
      .then(({ data }) => {
        if (data.length === 0) return
        setData(data[0])
      })
      .catch((error) => {
        console.log(error)
        toast.error('Erro ao buscar integração com evo crm.')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [assistantID])

  const install = useCallback(
    (values) => {
      setButtonLoading(true)
      createEvoCRMIntegration(parseInt(assistantID), {
        isActive: true,
        ...values,
      })
        .then(({ data }) => {
          setData(data)
          setConfigOpen(false)
        })
        .catch((error) => {
          console.log(error)
          toast.error('Erro ao instalar integração com evo crm.')
        })
        .finally(() => {
          setButtonLoading(false)
          form.resetFields()
        })
    },
    [assistantID]
  )

  const uninstall = useCallback(() => {
    setButtonLoading(true)
    deleteEvoCRMIntegration(assistantID, data.id)
      .then(() => {
        setData(undefined)
      })
      .catch((error) => {
        console.log(error)
        toast.error('Erro ao remover integração com evo crm.')
      })
      .finally(() => {
        setButtonLoading(false)
      })
  }, [data])

  const update = useCallback(
    (newData) => {
      setButtonLoading(true)
      updateEvoCRMIntegration(assistantID, data.id, newData)
        .then(({ data: updatedData }) => {
          setData({ ...updatedData })
          setConfigOpen(false)
          toast.success('Atualizado com sucesso!')
        })
        .catch((error) => {
          console.log(error)
          toast.error('Erro ao atualizar.')
        })
        .finally(() => {
          setButtonLoading(false)
          form.resetFields()
        })
    },
    [data]
  )

  const finishForm = useCallback(
    (values) => {
      if (!data) return install(values)

      update(values)
    },
    [data]
  )

  useEffect(() => {
    form.setFieldsValue(data)
  }, [data, form])

  return (
    <>
      <Modal
        title="Configurar EvoCRM"
        open={configOpen}
        cancelText={'Cancelar'}
        confirmLoading={buttonLoading}
        okText={'Salvar'}
        onOk={form.submit}
        onCancel={() => setConfigOpen(false)}
      >
        <br />

        <Form layout={'vertical'} form={form} onFinish={finishForm}>
          <Form.Item
            label={'Token'}
            help={
              <>
                <a
                  href={'https://evo.wts.chat/integration/token'}
                  target={'_blank'}
                >
                  Clique aqui
                </a>{' '}
                para encontrar seu token
              </>
            }
            name={'token'}
          >
            <Input.Password
              placeholder={'ID da instancia do evo crm'}
              autocomplete="off"
            />
          </Form.Item>

          <Form.Item
            label={'Criar pessoa automaticamente'}
            name={'autoCreateContact'}
            valuePropName={'checked'}
            help={'Cria a pessoa de forma automática na primeira mensagem'}
            initialValue={false}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label={'Criar Card automaticamente'}
            name={'autoCreateCard'}
            valuePropName={'checked'}
            help={'Cria o card de forma automática na primeira mensagem'}
            initialValue={false}
          >
            <Switch />
          </Form.Item>
          {autoCreateCard && data?.id && (
            <Form.Item
              label={'Painel Padrão'}
              name={'defaultPanelID'}
              help={'Em qual painel o card deve ser criado'}
            >
              <InputSearch
                searchField={'title'}
                showSearch={false}
                getCallback={() =>
                  getEvoCRMPanelsIntegration(assistantID, data.id)
                }
              />
            </Form.Item>
          )}
          <Form.Item
            label={'Integração Ativa'}
            name={'isActive'}
            valuePropName={'checked'}
            help={'Ativa/Desativa integração'}
            initialValue={true}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
      <Card
        loading={loading}
        title={
          <Typography.Title level={5} ellipsis={{ tooltip: label }}>
            {icon} {label}
          </Typography.Title>
        }
        extra={
          <Button
            type={data ? 'default' : 'primary'}
            loading={loading || buttonLoading}
            danger={!!data}
            onClick={() => (data ? uninstall() : setConfigOpen(true))}
            icon={data ? <CloseOutlined /> : <DownloadOutlined />}
          >
            {loading ? undefined : data ? 'Remover' : 'Instalar'}
          </Button>
        }
        actions={
          data
            ? [<SettingOutlined onClick={() => setConfigOpen(true)} />]
            : undefined
        }
      >
        <Typography>
          Configure o whatsapp com sua instancia do{' '}
          <a target={'_blank'} rel="noreferrer" href={'https://evo.wts.chat/'}>
            Evo CRM
          </a>
          .
        </Typography>
      </Card>
    </>
  )
}
